import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Monitor, Mic, Speaker, Radio, Usb, HardDrive, Tv, Volume2,
  RefreshCw, ChevronDown, ChevronUp, X, Check, AlertTriangle,
  Plug, Unplug, Video, Headphones, Music, Settings, Wifi
} from "lucide-react";

/**
 * DevicePanel — Complete hardware device inventory for the Régie.
 * Shows all connected video/audio devices, classified by type.
 * Hot-plug notifications, device categories, quick-add to sources.
 */

const CATEGORY_CONFIG = {
  capture_card: { label: "Cartes de capture", icon: Tv, color: "text-[#F97316]", bg: "bg-[#F97316]/10", border: "border-[#F97316]/30" },
  webcam: { label: "Webcams", icon: Video, color: "text-[#0099CC]", bg: "bg-[#0099CC]/10", border: "border-[#0099CC]/30" },
  audio_interface: { label: "Interfaces audio (entree)", icon: Music, color: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10", border: "border-[#8B5CF6]/30" },
  audio_interface_out: { label: "Interfaces audio (sortie)", icon: Headphones, color: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10", border: "border-[#8B5CF6]/30" },
  mic_pro: { label: "Microphones pro", icon: Mic, color: "text-[#10B981]", bg: "bg-[#10B981]/10", border: "border-[#10B981]/30" },
  mic_builtin: { label: "Micro integre", icon: Mic, color: "text-[#64748B]", bg: "bg-[#64748B]/10", border: "border-[#64748B]/30" },
  mic_other: { label: "Autres micros", icon: Mic, color: "text-[#94A3B8]", bg: "bg-[#94A3B8]/10", border: "border-[#94A3B8]/30" },
  hdmi_audio: { label: "Audio HDMI", icon: Monitor, color: "text-[#64748B]", bg: "bg-[#64748B]/10", border: "border-[#64748B]/30" },
  hdmi_audio_out: { label: "Sortie HDMI", icon: Monitor, color: "text-[#64748B]", bg: "bg-[#64748B]/10", border: "border-[#64748B]/30" },
  speaker_builtin: { label: "Haut-parleurs", icon: Speaker, color: "text-[#64748B]", bg: "bg-[#64748B]/10", border: "border-[#64748B]/30" },
  speaker_other: { label: "Autres sorties", icon: Volume2, color: "text-[#94A3B8]", bg: "bg-[#94A3B8]/10", border: "border-[#94A3B8]/30" },
};

const KIND_LABELS = {
  videoinput: "Video",
  audioinput: "Entree audio",
  audiooutput: "Sortie audio",
};

export default function DevicePanel({
  devices,
  captureCards,
  audioInterfaces,
  permissionGranted,
  lastEvent,
  scanning,
  onScan,
  onAddVideoSource,
  onSelectAudioInput,
  onSelectAudioOutput,
  onClose,
}) {
  const [expandedCategories, setExpandedCategories] = useState(new Set(["capture_card", "webcam", "audio_interface", "mic_pro"]));
  const [selectedAudioInput, setSelectedAudioInput] = useState(null);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState(null);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // Group devices by category
  const grouped = {};
  for (const d of devices) {
    if (d.isDefault || d.isCommunications) continue;
    if (!grouped[d.category]) grouped[d.category] = [];
    grouped[d.category].push(d);
  }

  // Priority order
  const categoryOrder = [
    "capture_card", "webcam", "audio_interface", "mic_pro",
    "audio_interface_out", "mic_builtin", "mic_other",
    "hdmi_audio", "hdmi_audio_out", "speaker_builtin", "speaker_other",
  ];

  const videoCount = devices.filter(d => d.kind === "videoinput" && !d.isDefault).length;
  const audioInCount = devices.filter(d => d.kind === "audioinput" && !d.isDefault && !d.isCommunications).length;
  const audioOutCount = devices.filter(d => d.kind === "audiooutput" && !d.isDefault && !d.isCommunications).length;

  return (
    <div className="bg-[#060B14] border border-[#1A2332] rounded-2xl overflow-hidden max-h-[80vh] flex flex-col" data-testid="device-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A2332] bg-[#0B1120] flex-shrink-0">
        <div className="flex items-center gap-2">
          <Usb className="w-4 h-4 text-[#0099CC]" />
          <span className="text-white text-sm font-bold">Peripheriques</span>
          <span className="text-[10px] text-[#64748B] ml-1">{devices.filter(d => !d.isDefault && !d.isCommunications).length} detecte(s)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            onClick={() => onScan(true)}
            disabled={scanning}
            className="h-7 text-[10px] bg-[#0099CC]/20 hover:bg-[#0099CC]/30 text-[#0099CC] border border-[#0099CC]/30"
            data-testid="device-scan-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scan..." : "Scanner"}
          </Button>
          {onClose && (
            <button onClick={onClose} className="text-[#64748B] hover:text-white p-1 rounded">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Hot-plug notification */}
      {lastEvent && (
        <div className={`mx-3 mt-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 animate-pulse ${
          lastEvent.type === "connected"
            ? "bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981]"
            : "bg-red-500/10 border border-red-500/30 text-red-400"
        }`} data-testid="device-hotplug-notification">
          {lastEvent.type === "connected" ? <Plug className="w-3.5 h-3.5" /> : <Unplug className="w-3.5 h-3.5" />}
          {lastEvent.type === "connected"
            ? `${lastEvent.device?.brand || "Peripherique"} connecte: ${lastEvent.device?.label}`
            : "Peripherique deconnecte"
          }
        </div>
      )}

      {/* Permission request */}
      {!permissionGranted && (
        <div className="mx-3 mt-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="device-permission-request">
          <div className="flex items-center gap-2 text-amber-400 text-xs mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="font-semibold">Permission requise</span>
          </div>
          <p className="text-[#94A3B8] text-[10px] mb-2">
            Pour voir les noms des peripheriques, autorisez l'acces camera/micro.
          </p>
          <Button
            size="sm"
            onClick={() => onScan(true)}
            className="h-6 text-[10px] bg-amber-500 hover:bg-amber-600 text-black w-full"
            data-testid="device-grant-permission-btn"
          >
            Autoriser l'acces
          </Button>
        </div>
      )}

      {/* Summary bar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[#1A2332]">
        <div className="flex items-center gap-1.5">
          <Video className="w-3 h-3 text-[#0099CC]" />
          <span className="text-[10px] text-[#94A3B8]">{videoCount} video</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Mic className="w-3 h-3 text-[#10B981]" />
          <span className="text-[10px] text-[#94A3B8]">{audioInCount} entree(s)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Volume2 className="w-3 h-3 text-[#8B5CF6]" />
          <span className="text-[10px] text-[#94A3B8]">{audioOutCount} sortie(s)</span>
        </div>
        {captureCards.length > 0 && (
          <div className="flex items-center gap-1.5 ml-auto">
            <Tv className="w-3 h-3 text-[#F97316]" />
            <span className="text-[10px] text-[#F97316] font-bold">{captureCards.length} carte(s)</span>
          </div>
        )}
      </div>

      {/* Device list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="device-list">
        {categoryOrder.map(cat => {
          const devs = grouped[cat];
          if (!devs || devs.length === 0) return null;
          const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.speaker_other;
          const CatIcon = config.icon;
          const expanded = expandedCategories.has(cat);

          return (
            <div key={cat} className={`rounded-lg border ${config.border} overflow-hidden`}>
              <button
                onClick={() => toggleCategory(cat)}
                className={`w-full flex items-center gap-2 px-3 py-2 ${config.bg} hover:brightness-110 transition-all`}
                data-testid={`device-cat-${cat}`}
              >
                <CatIcon className={`w-3.5 h-3.5 ${config.color}`} />
                <span className={`text-xs font-semibold ${config.color}`}>{config.label}</span>
                <span className="text-[9px] text-[#64748B] ml-1">({devs.length})</span>
                <div className="ml-auto">
                  {expanded ? <ChevronUp className="w-3 h-3 text-[#64748B]" /> : <ChevronDown className="w-3 h-3 text-[#64748B]" />}
                </div>
              </button>

              {expanded && (
                <div className="px-2 py-1.5 space-y-1 bg-[#060B14]">
                  {devs.map((d, i) => (
                    <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#0B1120] group transition-all" data-testid={`device-${d.id}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${config.color.replace("text-", "bg-")}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[#E2E8F0] text-[11px] truncate">{d.label}</p>
                        <div className="flex items-center gap-2">
                          {d.brand && <span className="text-[8px] text-[#F97316] font-bold uppercase">{d.brand}</span>}
                          <span className="text-[8px] text-[#64748B]">{KIND_LABELS[d.kind]}</span>
                        </div>
                      </div>

                      {/* Quick actions */}
                      {d.kind === "videoinput" && onAddVideoSource && (
                        <button
                          onClick={() => onAddVideoSource(d)}
                          className="opacity-0 group-hover:opacity-100 text-[#0099CC] hover:text-white text-[9px] bg-[#0099CC]/10 px-2 py-0.5 rounded transition-opacity"
                          data-testid={`device-add-${d.id}`}
                        >
                          + Source
                        </button>
                      )}
                      {d.kind === "audioinput" && onSelectAudioInput && (
                        <button
                          onClick={() => { setSelectedAudioInput(d.id); onSelectAudioInput(d); }}
                          className={`text-[9px] px-2 py-0.5 rounded transition-all ${
                            selectedAudioInput === d.id
                              ? "bg-[#10B981] text-white"
                              : "opacity-0 group-hover:opacity-100 text-[#10B981] bg-[#10B981]/10"
                          }`}
                          data-testid={`device-select-audio-in-${d.id}`}
                        >
                          {selectedAudioInput === d.id ? <Check className="w-3 h-3 inline" /> : "Utiliser"}
                        </button>
                      )}
                      {d.kind === "audiooutput" && onSelectAudioOutput && (
                        <button
                          onClick={() => { setSelectedAudioOutput(d.id); onSelectAudioOutput(d); }}
                          className={`text-[9px] px-2 py-0.5 rounded transition-all ${
                            selectedAudioOutput === d.id
                              ? "bg-[#8B5CF6] text-white"
                              : "opacity-0 group-hover:opacity-100 text-[#8B5CF6] bg-[#8B5CF6]/10"
                          }`}
                          data-testid={`device-select-audio-out-${d.id}`}
                        >
                          {selectedAudioOutput === d.id ? <Check className="w-3 h-3 inline" /> : "Ecouter"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(grouped).length === 0 && !scanning && (
          <div className="text-center py-8">
            <Usb className="w-8 h-8 text-[#1A2332] mx-auto mb-3" />
            <p className="text-[#64748B] text-xs">Aucun peripherique detecte</p>
            <p className="text-[#4A5568] text-[10px] mt-1">Cliquez "Scanner" pour detecter vos appareils</p>
          </div>
        )}
      </div>
    </div>
  );
}
